import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import IconButton from '@mui/material/IconButton';
import {
  $getSelection, $isRangeSelection, CAN_REDO_COMMAND, CAN_UNDO_COMMAND, FORMAT_ELEMENT_COMMAND, FORMAT_TEXT_COMMAND, REDO_COMMAND,
  SELECTION_CHANGE_COMMAND, UNDO_COMMAND,
} from 'lexical';
import InsertVariablePlugin from './InsertVariablePlugin';
import styles from '../CustomRichText.module.scss';

const LowPriority = 1;

const TEXT_FORMAT_BOLD = 'bold';
const TEXT_FORMAT_ITALIC = 'italic';
const TEXT_FORMAT_UNDERLINE = 'underline';
const LIST_TYPE_BULLET = 'bullet';
const LIST_TYPE_NUMBER = 'number';

function Divider() {
  return <div className={styles.Divider} />;
}

type ToolbarPluginProps = {
  handleOpenVariables?: () => void;
  variableSelected?: string;
  title?: string;
};

const ToolbarPlugin = (props: ToolbarPluginProps) => {
  const { handleOpenVariables, variableSelected, title } = props;
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [toolbarState, setToolbarState] = useState({
    canUndo: false,
    canRedo: false,
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isOrderedList: false,
    isNumberList: false,
    isLink: false,
  });

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();

      const listNodeParent = $findMatchingParent(anchorNode, (node) => $isListNode(node));

      let isOrderedList = false;
      let isNumberList = false;

      if (listNodeParent && $isListNode(listNodeParent)) {
        const listType = listNodeParent.getListType();
        isOrderedList = listType === LIST_TYPE_BULLET;
        isNumberList = listType === LIST_TYPE_NUMBER;
      }

      const node = $findMatchingParent(anchorNode, (parent) => $isLinkNode(parent));

      setToolbarState((prev) => ({
        ...prev,
        isBold: selection.hasFormat(TEXT_FORMAT_BOLD),
        isItalic: selection.hasFormat(TEXT_FORMAT_ITALIC),
        isUnderline: selection.hasFormat(TEXT_FORMAT_UNDERLINE),
        isOrderedList,
        isNumberList,
        isLink: $isLinkNode(node),
      }));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();

          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setToolbarState((prev) => ({ ...prev, canUndo: payload }));

          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setToolbarState((prev) => ({ ...prev, canRedo: payload }));

          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, $updateToolbar]);

  const enterUrl = () => {
    const url = prompt('URL:');

    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    }
  };

  return (
    <div className={styles.Toolbar} ref={toolbarRef}>
      <div className={styles.ToolbarRow}>
        <p className={styles.Title}> {title}</p>
      </div>
      <div className={styles.ToolbarRow}>
        <div className={styles.ToolbarRow}>

          <button
            disabled={!toolbarState.canUndo}
            onClick={() => {
              editor.dispatchCommand(UNDO_COMMAND, undefined);
            }}
            aria-label="Undo"
          >
            <i className="di icon-rueckgaengig-undo" />
          </button>
          <button
            disabled={!toolbarState.canRedo}
            onClick={() => {
              editor.dispatchCommand(REDO_COMMAND, undefined);
            }}
            aria-label="Redo"
          >
            <i className="di icon-pfeil-wiederherstellen-redo" />
          </button>
          <Divider />
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, TEXT_FORMAT_BOLD);
            }}
            className={toolbarState.isBold ? styles.Active : ''}
            aria-label="Format Bold"
          >
            <i className="di icon-schriftschnitt-fett" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, TEXT_FORMAT_ITALIC);
            }}
            className={toolbarState.isItalic ? styles.Active : ''}
            aria-label="Format Italics"
          >
            <i className="di icon-schriftschnitt-kursiv" />
          </button>
          <button
            onClick={() => { editor.dispatchCommand(FORMAT_TEXT_COMMAND, TEXT_FORMAT_UNDERLINE); }}
            className={toolbarState.isUnderline ? styles.Active : ''}
            aria-label="Format Underline"
          >
            <i className="di icon-schriftschnitt-unterstrichen" />
          </button>
          <Divider />
          <IconButton
            className={`${styles.ToolbarItem} ${toolbarState.isOrderedList ? styles.Active : ''}`}
            color="inherit"
            size="small"
            aria-label="Bullet List"
            onClick={() => {
              if (toolbarState.isOrderedList) {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
              } else {
                editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
              }
            }}
          >
            <FormatListBulletedIcon />
          </IconButton>
          <IconButton
            className={`${styles.ToolbarItem} ${toolbarState.isNumberList ? styles.Active : ''}`}
            color="inherit"
            size="small"
            aria-label="Number List"
            onClick={() => {
              if (toolbarState.isNumberList) {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
              } else {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
              }
            }}
          >
            <FormatListNumberedIcon />
          </IconButton>
          <Divider />
          <IconButton
            className={`${styles.ToolbarItem} ${toolbarState.isLink ? styles.Active : ''}`}
            color="inherit"
            size="small"
            aria-label="Insert Link"
            onClick={() => enterUrl()}
          >
            <InsertLinkIcon />
          </IconButton>
          <IconButton
            className={styles.ToolbarItem}
            color="inherit"
            size="small"
            aria-label="Remove Link"
            disabled={!toolbarState.isLink}
            onClick={() => {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            }}
          >
            <LinkOffIcon />
          </IconButton>
          <Divider />
          <IconButton
            className={styles.ToolbarItem}
            color="inherit"
            size="small"
            aria-label="Left Align"
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
            }}
          >
            <FormatAlignLeftIcon />
          </IconButton>
          <IconButton
            className={styles.IconClear}
            color="inherit"
            size="small"
            aria-label="Justify Align"
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
            }}
          >
            <FormatAlignCenterIcon />
          </IconButton>
          <IconButton
            className={styles.IconClear}
            color="inherit"
            size="small"
            aria-label="Right Align"
            onClick={() => {
              editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
            }}
          >
            <FormatAlignRightIcon />
          </IconButton>
        </div>
        {handleOpenVariables && <InsertVariablePlugin handleOpenVariables={handleOpenVariables} variableSelected={variableSelected} />}
      </div>
    </div>
  );
};

export default ToolbarPlugin;
